import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export let APP_CONFIG: any = {};
export let APP_CONFIG_LOCAL: any = {};

@Injectable({
  providedIn: 'root'
})

export class AppConfigService {
  private appConfig: any;
  private appConfigLocal: any;

  tpaKillswitchValue = 'APIC';
  buyblueKillswitchValue = 'APIC';

  constructor(private injector: Injector) { }

  loadAppConfig() {
    const http = this.injector.get(HttpClient);

    return firstValueFrom(http.get('./assets/config/app-config.json'))
      .then(async (data) => {
        this.appConfig = data;
        APP_CONFIG = Object.assign(this.appConfig);
        // Await killswitch values so they are ready before app initialization completes
        await this.initializeKillswitchValues();
      })
      .catch((err) => {
        console.error(err);
      })
  }

  loadLocalConfig() {
    const http = this.injector.get(HttpClient);

    return firstValueFrom(http.get('./assets/config/app-config-local.json'))
      .then((data) => {
        this.appConfigLocal = data;
        APP_CONFIG_LOCAL = Object.assign(this.appConfigLocal);
      })
      .catch((err) => {
        console.error(err);
      })
  }

  /**
   * Fetch killswitch values from AEM URLs and store them in-memory.
   * Called automatically after app config is loaded.
   * For local environment, uses KILLSWITCH_VALUE from config instead of AEM.
   */
  
  private getDefaultTpaKillswitchValue(): string {
    return (APP_CONFIG.DEFAULT_TPA_KILLSWITCH_VALUE || 'APIC').toString().trim().toUpperCase();
  }

  private getDefaultBuyblueKillswitchValue(): string {
    return (APP_CONFIG.DEFAULT_BUYBLUE_KILLSWITCH_VALUE || 'APIC').toString().trim().toUpperCase();
  }

  private async initializeKillswitchValues(): Promise<void> {
    const http = this.injector.get(HttpClient);
    this.tpaKillswitchValue = this.getDefaultTpaKillswitchValue();
    this.buyblueKillswitchValue = this.getDefaultBuyblueKillswitchValue();

    // Check if running in local environment
    const isLocal = APP_CONFIG.ENV_NAME?.toLowerCase() === 'local';

    if (isLocal && APP_CONFIG.KILLSWITCH_VALUE) {
      // For local environment, use the KILLSWITCH_VALUE from config directly
      const killswitchValue = APP_CONFIG.KILLSWITCH_VALUE.toUpperCase();
      this.tpaKillswitchValue = killswitchValue;
      this.buyblueKillswitchValue = killswitchValue;
      console.log('Local Environment - Using config KILLSWITCH_VALUE:', killswitchValue);
      return; // Skip AEM URL fetching for local
    }

    // For non-local environments, fetch from AEM URLs in parallel
    const tpaPromise = APP_CONFIG.KILL_SWITCH_AEM_TPA_URL
      ? firstValueFrom(http.get<any>(APP_CONFIG.KILL_SWITCH_AEM_TPA_URL))
          .then((response) => {
            this.tpaKillswitchValue = this.extractKillSwitchValue(response, 'cloudCSAdminToggle').toUpperCase();
          })
          .catch(() => {
            this.tpaKillswitchValue = this.getDefaultTpaKillswitchValue();
          })
      : Promise.resolve();

    const buyBluePromise = APP_CONFIG.KILL_SWITCH_AEM_BUYBLUE_URL
      ? firstValueFrom(http.get<any>(APP_CONFIG.KILL_SWITCH_AEM_BUYBLUE_URL))
          .then((response) => {
            this.buyblueKillswitchValue = this.extractBuyBlueKillSwitchValue(response, 'cloudCSAdminToggle').toUpperCase();
            console.log('BuyBlue Killswitch from AEM:', this.buyblueKillswitchValue);
          })
          .catch(() => {
            this.buyblueKillswitchValue = this.getDefaultBuyblueKillswitchValue();
          })
      : Promise.resolve();

    await Promise.all([tpaPromise, buyBluePromise]);
  }

  private extractKillSwitchValue(response: any, key: string): string {
    const fallback = this.getDefaultTpaKillswitchValue();
    const killSwitches: any[] = response?.cmsResponse?.killSwitches ?? [];
    for (const entry of killSwitches) {
      try {
        const parsed = typeof entry === 'string' ? JSON.parse(entry) : entry;
        if (parsed?.key === key) {
          return (parsed.value || fallback).trim();
        }
      } catch {
        // skip malformed entries
      }
    }
    return fallback;
  }

  private extractBuyBlueKillSwitchValue(response: any, key: string): string {
    const fallback = this.getDefaultBuyblueKillswitchValue();
    const killSwitches: { key: string; value: string }[] =
      response?.cmsResponse?.killswitch?.killSwitches ?? [];
    const match = killSwitches.find((entry) => entry?.key === key);
    return (match?.value || fallback).trim();
  }

}
